import type { KeyboardEvent, RefObject } from 'react';
import { Command, Search } from 'lucide-react';
import type { SearchResults } from '../../api/client';
import { SearchResultsDropdown } from './SearchResultsDropdown';

interface TopBarSearchBoxProps {
  debouncedTerm: string;
  isMobile: boolean;
  results?: SearchResults;
  searchInputRef: RefObject<HTMLInputElement | null>;
  searchRef: RefObject<HTMLDivElement | null>;
  searchTerm: string;
  semanticMode: boolean;
  showResults: boolean;
  onCommandPaletteOpen: () => void;
  onFullSearch: () => void;
  onSearchTermChange: (term: string) => void;
  onSelect: (path: string) => void;
  onSemanticModeChange: (enabled: boolean) => void;
  onShowResultsChange: (show: boolean) => void;
  onSubmitSearch: () => void;
}

export function TopBarSearchBox({
  debouncedTerm,
  isMobile,
  results,
  searchInputRef,
  searchRef,
  searchTerm,
  semanticMode,
  showResults,
  onCommandPaletteOpen,
  onFullSearch,
  onSearchTermChange,
  onSelect,
  onSemanticModeChange,
  onShowResultsChange,
  onSubmitSearch,
}: TopBarSearchBoxProps) {
  const handleChange = (term: string) => {
    onSearchTermChange(term);
    onShowResultsChange(true);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchTerm.trim().length >= 2) {
      e.preventDefault();
      onSubmitSearch();
    }
  };

  const dropdown = (
    <SearchResultsDropdown
      debouncedTerm={debouncedTerm}
      results={results}
      showResults={showResults}
      onFullSearch={onFullSearch}
      onSelect={onSelect}
    />
  );

  if (isMobile) {
    return (
      <div ref={searchRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => onShowResultsChange(true)}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            padding: '0.5rem 0.6rem 0.5rem 2.2rem',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: '0.85rem',
            outline: 'none',
            boxSizing: 'border-box',
            height: 44,
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: '0.65rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Search size={16} />
        </span>
        {dropdown}
      </div>
    );
  }

  return (
    <div ref={searchRef} style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
      <input
        ref={searchInputRef}
        type="text"
        placeholder="Search contacts, matters & documents..."
        value={searchTerm}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => onShowResultsChange(true)}
        onKeyDown={handleKeyDown}
        style={{
          width: '100%',
          padding: '0.45rem 5.5rem 0.45rem 2.1rem',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border)',
          background: 'var(--bg)',
          color: 'var(--text)',
          fontSize: '0.85rem',
          outline: 'none',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        }}
      />
      <span
        onClick={onCommandPaletteOpen}
        style={{
          position: 'absolute',
          right: results?.semantic_enabled ? '2.5rem' : '0.5rem',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.15rem',
          padding: '0.1rem 0.35rem',
          fontSize: '0.6rem',
          fontWeight: 600,
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          background: 'var(--bg)',
        }}
      >
        <Command size={10} /> K
      </span>
      <span
        style={{
          position: 'absolute',
          left: '0.65rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-muted)',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Search size={15} />
      </span>
      {results?.semantic_enabled && (
        <button
          onClick={() => onSemanticModeChange(!semanticMode)}
          title={semanticMode ? 'Semantic search ON' : 'Semantic search OFF'}
          style={{
            position: 'absolute',
            right: '0.45rem',
            top: '50%',
            transform: 'translateY(-50%)',
            padding: '0.15rem 0.45rem',
            fontSize: '0.65rem',
            fontWeight: 700,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            cursor: 'pointer',
            background: semanticMode ? 'var(--primary)' : 'var(--bg-elevated, #e5e5e5)',
            color: semanticMode ? '#fff' : 'var(--text-muted)',
            transition: 'all 0.15s ease',
          }}
        >
          AI
        </button>
      )}
      {dropdown}
    </div>
  );
}
